import { useEffect, useMemo, useRef, useState } from 'react';

const sanitizeFieldClass = field =>
  String(field || '').replace(/[^a-zA-Z0-9_-]/g, '-');

const uniqueValues = values => Array.from(new Set(values.filter(Boolean)));

/** Frozen columns must be a prefix of `fields` (left-to-right pin order). */
const toFrozenPrefix = (draft, fields) => {
  const inDraft = new Set(draft.filter(f => fields.includes(f)));
  const out = [];
  for (const f of fields) {
    if (inDraft.has(f)) out.push(f);
    else break;
  }
  return out;
};

/** Length k: first k columns of `af` match frozen draft prefix (same rules as pin UI). */
const getDraftPrefixLength = (draft, af) => {
  const d = draft;
  if (!d?.length) return 0;
  if (d.length > af.length) return toFrozenPrefix(d, af).length;
  if (af.slice(0, d.length).every((f, i) => Object.is(f, d[i]))) {
    return d.length;
  }
  return toFrozenPrefix(d, af).length;
};

const areArraysEqual = (a = [], b = []) => {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((value, index) => value === b[index]);
};

export default function useTableFreeze({
  tableRef,
  orderedFields = [],
  availableColumns = [],
  positionDeps = [],
  classPrefix = 'pf-table-freeze',
  initialFreezeHeader,
  initialFrozenColumns,
  onConfirmFreeze,
}) {
  const freezeButtonRef = useRef(null);
  const freezePopoverRef = useRef(null);

  const [showFreezePopover, setShowFreezePopover] = useState(false);
  const [freezeState, setFreezeState] = useState(() => {
    const h = initialFreezeHeader ?? false;
    const cols = Array.isArray(initialFrozenColumns)
      ? initialFrozenColumns
      : [];
    return {
      freezeHeader: h,
      freezeHeaderDraft: h,
      frozenColumns: cols,
      frozenColumnsDraft: cols,
    };
  });

  const { freezeHeader, freezeHeaderDraft, frozenColumns, frozenColumnsDraft } =
    freezeState;

  /* Do not reset draft from server props while the popover is open — the fetch
     that runs on open updates initialFrozenColumns and would wipe in-progress
     toggles so checkmarks never stick. */
  useEffect(() => {
    if (showFreezePopover) {
      return;
    }
    const h = initialFreezeHeader ?? false;
    const cols = Array.isArray(initialFrozenColumns)
      ? initialFrozenColumns
      : [];
    setFreezeState({
      freezeHeader: h,
      freezeHeaderDraft: h,
      frozenColumns: cols,
      frozenColumnsDraft: cols,
    });
  }, [initialFreezeHeader, initialFrozenColumns, showFreezePopover]);

  /* Primitive signature so deps do not churn when `availableColumns` is a new
     array reference each render with the same columns. */
  const freezeFieldsSignature = availableColumns
    .map(c => `${String(c.field)}\t${String(c.header ?? '')}`)
    .join('\0');

  const availableFields = useMemo(
    () => availableColumns.map(col => col.field),
    [freezeFieldsSignature]
  );

  const availableFieldsRef = useRef(availableFields);
  availableFieldsRef.current = availableFields;

  const appliedFrozenColumns = useMemo(
    () => uniqueValues(frozenColumns),
    [frozenColumns]
  );

  const pinPrefixColumnCount = useMemo(
    () => getDraftPrefixLength(frozenColumnsDraft, availableFields),
    [frozenColumnsDraft, availableFields]
  );

  useEffect(() => {
    const af = availableFieldsRef.current;
    setFreezeState(prev => {
      const nextCols = toFrozenPrefix(
        prev.frozenColumns.filter(field => af.includes(field)),
        af
      );
      const nextDraft = toFrozenPrefix(
        prev.frozenColumnsDraft.filter(field => af.includes(field)),
        af
      );
      const colsChanged = !areArraysEqual(prev.frozenColumns, nextCols);
      const draftChanged = !areArraysEqual(prev.frozenColumnsDraft, nextDraft);
      if (!colsChanged && !draftChanged) return prev;
      return {
        ...prev,
        frozenColumns: colsChanged ? nextCols : prev.frozenColumns,
        frozenColumnsDraft: draftChanged ? nextDraft : prev.frozenColumnsDraft,
      };
    });
  }, [freezeFieldsSignature]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (!showFreezePopover) {
        return;
      }

      if (
        freezePopoverRef.current?.contains(event.target) ||
        freezeButtonRef.current?.contains(event.target)
      ) {
        return;
      }

      setShowFreezePopover(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showFreezePopover]);

  useEffect(() => {
    const raw = tableRef?.current?.getElement?.() || tableRef?.current;
    const tableElement =
      raw?.nodeName === 'TABLE' ? raw : (raw?.querySelector?.('table') ?? raw);
    let rafId;

    if (!tableElement?.querySelector) {
      return;
    }

    const clearFreezePlacementStyles = cell => {
      cell.style.left = '';
      cell.style.width = '';
      cell.style.minWidth = '';
      cell.style.maxWidth = '';
      cell.style.boxSizing = '';
      cell.style.overflow = '';
      cell.style.zIndex = '';
    };

    const applyFreezePosition = () => {
      tableElement
        .querySelectorAll(`[class*="${classPrefix}"]`)
        .forEach(clearFreezePlacementStyles);
      tableElement.querySelectorAll('thead th, tbody td').forEach(cell => {
        if (cell.style.left) {
          clearFreezePlacementStyles(cell);
        }
      });

      const headerRow = tableElement.querySelector('thead > tr');
      const headerCells = headerRow
        ? headerRow.querySelectorAll(':scope > th')
        : tableElement.querySelectorAll('thead > tr > th');
      let leftOffset = 0;

      orderedFields.forEach((field, index) => {
        if (!appliedFrozenColumns.includes(field)) {
          return;
        }

        const freezeStackIndex = appliedFrozenColumns.indexOf(field);
        const colWidth = headerCells[index]?.offsetWidth || 0;
        const freezeClassName = `${classPrefix}-${sanitizeFieldClass(field)}`;
        const matchingCells = tableElement.querySelectorAll(
          `.${freezeClassName}`
        );

        matchingCells.forEach(cell => {
          cell.style.left = `${leftOffset}px`;
          cell.style.width = `${colWidth}px`;
          cell.style.minWidth = `${colWidth}px`;
          cell.style.maxWidth = `${colWidth}px`;
          cell.style.boxSizing = 'border-box';
          cell.style.overflow = 'hidden';
          const inThead = Boolean(cell.closest && cell.closest('thead'));
          /* Thead sticky cells must stack above tbody frozen cells (fixes header overlap on scroll). */
          cell.style.zIndex = String(
            inThead ? 46 + freezeStackIndex : 6 + freezeStackIndex * 2
          );
        });

        leftOffset += colWidth;
      });
    };

    applyFreezePosition();
    rafId = requestAnimationFrame(() => {
      applyFreezePosition();
    });
    const timeoutId = setTimeout(applyFreezePosition, 150);
    const lateId =
      appliedFrozenColumns.length > 0
        ? setTimeout(applyFreezePosition, 450)
        : null;
    window.addEventListener('resize', applyFreezePosition);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      if (lateId) clearTimeout(lateId);
      window.removeEventListener('resize', applyFreezePosition);
    };
  }, [
    tableRef,
    classPrefix,
    orderedFields,
    appliedFrozenColumns,
    ...positionDeps,
  ]);

  const setFreezeHeaderDraft = valueOrUpdater => {
    setFreezeState(prev => ({
      ...prev,
      freezeHeaderDraft:
        typeof valueOrUpdater === 'function'
          ? valueOrUpdater(prev.freezeHeaderDraft)
          : valueOrUpdater,
    }));
  };

  const togglePopover = () => {
    setFreezeState(prev => ({
      ...prev,
      freezeHeaderDraft: prev.freezeHeader,
      frozenColumnsDraft: prev.frozenColumns,
    }));
    setShowFreezePopover(prev => !prev);
  };

  /**
   * Set column checked state from PrimeReact `onChange` (`e.checked`) — avoids
   * toggle desync / repeat-click issues with controlled Checkbox + focus.
   */
  const setDraftColumnChecked = (columnIndex, nextChecked) => {
    setFreezeState(prev => {
      const af = availableFieldsRef.current;
      if (!af.length || columnIndex < 0 || columnIndex >= af.length) {
        return prev;
      }

      const k = getDraftPrefixLength(prev.frozenColumnsDraft, af);

      if (nextChecked) {
        if (columnIndex !== k) return prev;
        return { ...prev, frozenColumnsDraft: af.slice(0, columnIndex + 1) };
      }

      if (columnIndex >= k) return prev;
      return { ...prev, frozenColumnsDraft: af.slice(0, columnIndex) };
    });
  };

  /** Toggle by column index (legacy); prefer setDraftColumnChecked from UI. */
  const toggleDraftColumnIndex = columnIndex => {
    setFreezeState(prev => {
      const af = availableFieldsRef.current;
      if (!af.length || columnIndex < 0 || columnIndex >= af.length) {
        return prev;
      }

      const k = getDraftPrefixLength(prev.frozenColumnsDraft, af);
      const isFrozen = columnIndex < k;

      if (isFrozen) {
        return { ...prev, frozenColumnsDraft: af.slice(0, columnIndex) };
      }

      if (columnIndex !== k) return prev;
      return { ...prev, frozenColumnsDraft: af.slice(0, columnIndex + 1) };
    });
  };

  const confirmFreeze = () => {
    const newHeader = freezeHeaderDraft;
    const newColumns = [...frozenColumnsDraft];
    if (typeof onConfirmFreeze === 'function') {
      onConfirmFreeze(newHeader, newColumns);
    }
    setFreezeState(prev => ({
      ...prev,
      freezeHeader: newHeader,
      frozenColumns: newColumns,
    }));
    setShowFreezePopover(false);
  };

  const getColumnClassName = (field, className = '') => {
    const isFrozen = appliedFrozenColumns.includes(field);

    if (!isFrozen) {
      return className;
    }

    const freezeClasses = `${classPrefix}-col ${classPrefix}-${sanitizeFieldClass(field)}`;

    return `${className} ${freezeClasses}`.trim();
  };

  const freezeOptions = availableColumns.map((col, index) => ({
    field: col.field,
    header: col.header,
    columnIndex: index,
    checked: index < pinPrefixColumnCount,
    disabled: index > pinPrefixColumnCount,
  }));

  return {
    freezeButtonRef,
    freezePopoverRef,
    showFreezePopover,
    togglePopover,
    freezeHeader,
    freezeHeaderDraft,
    setFreezeHeaderDraft,
    freezeOptions,
    setDraftColumnChecked,
    toggleDraftColumnIndex,
    confirmFreeze,
    frozenCount: pinPrefixColumnCount,
    appliedFrozenColumns,
    tableClassName: `${classPrefix}-table ${
      freezeHeader ? `${classPrefix}-header-enabled` : ''
    }`.trim(),
    getColumnClassName,
  };
}
